/**
 * What `arpaymentmethods` stores about a method saved through Stripe, from what the server read
 * back from Stripe (describeStripePaymentMethod → sdk-ar's SavedPaymentMethodDetails). Pure, so
 * the shaping can be proved without mounting Stripe's Payment Element.
 */

/** sdk-ar's SavedPaymentMethodDetails, as the function returns it. */
export interface SavedPaymentMethodDetails {
  gatewayToken: string;
  type: 'card' | 'ach';
  /** The last four digits as a number — the row's `account_number` column. */
  last4: number;
  /** Stripe's brand: 'visa' | 'mastercard' | 'amex' | 'discover' | … */
  cardType?: string;
  /** MMYY as a number (1225 for 12/25). */
  expiration?: number;
  routingNumber?: number;
  /** 'checking' | 'savings' */
  accountType?: string;
  bankName?: string;
  /** 'individual' | 'company' */
  accountHolderType?: string;
}

/** `arpaymentmethods.source` for every row the Stripe form writes. */
export const STRIPE_PAYMENT_ELEMENT_SOURCE = 'stripe-payment-element';

/**
 * The row columns for a saved method. The browser never supplies any of these itself — the
 * server read them from Stripe after confirmation — and `gateway` is always 'stripe', which is
 * what lets the charge path tell this row from one saved under iCheck.
 */
export const toPaymentMethodRow = (details: SavedPaymentMethodDetails) => ({
  type: details.type,
  gateway: 'stripe',
  gatewayToken: details.gatewayToken,
  accountType: details.accountType ?? null,
  accountNumber: details.last4,
  cardType: details.cardType ?? null,
  expiration: details.expiration ?? null,
  routingNumber: details.routingNumber ?? null,
  bankName: details.bankName ?? null,
  accountHolderType: details.accountHolderType ?? null,
  source: STRIPE_PAYMENT_ELEMENT_SOURCE,
});
